/**
 * Tiles Workshop — promoted experiences rail
 * Pointer dragging for [data-tw-drag-scroll] rows. Shared by the block (product page) and the
 * section (any page); safe to load twice, the module URL is deduplicated by the browser.
 */
/**
 * Drag to scroll for horizontal rows marked [data-tw-drag-scroll].
 *
 * Impact ships no pointer dragging for scroll rows, and with the scrollbar hidden a mouse user
 * would otherwise have only the wheel. A short drag scrolls; a click that never moves still
 * follows the card link. Touch keeps native scrolling — the handler only runs for mouse and pen.
 */
(() => {
  const THRESHOLD = 6;

  document.addEventListener('pointerdown', (event) => {
    if (event.pointerType === 'touch' || event.button !== 0) return;

    const row = event.target.closest('[data-tw-drag-scroll]');
    if (!row || row.scrollWidth <= row.clientWidth) return;

    const startX = event.clientX;
    const startScroll = row.scrollLeft;
    let dragging = false;

    const move = (moveEvent) => {
      const delta = moveEvent.clientX - startX;
      if (!dragging && Math.abs(delta) < THRESHOLD) return;
      if (!dragging) {
        dragging = true;
        row.classList.add('is-dragging');
        row.setPointerCapture(event.pointerId);
      }
      row.scrollLeft = startScroll - delta;
    };

    const stop = () => {
      row.removeEventListener('pointermove', move);
      row.removeEventListener('pointerup', stop);
      row.removeEventListener('pointercancel', stop);
      if (!dragging) return;
      // Let the click that ends the gesture be swallowed before the cards accept clicks again.
      setTimeout(() => row.classList.remove('is-dragging'), 0);
    };

    row.addEventListener('pointermove', move);
    row.addEventListener('pointerup', stop);
    row.addEventListener('pointercancel', stop);
  });
})();

/**
 * Arrows for the row of cards.
 *
 * The row scrolls sideways with the scrollbar hidden, so with more cards than fit the rest were there
 * but easy to miss. The arrows step one screen of cards at a time and disappear when the cards all
 * fit, and one at a time at either end. Listeners sit on the document because Shopify's related
 * products arrive after the page and the editor redraws sections.
 */
(() => {
  const SLACK = 8;

  const sync = (wrap) => {
    const row = wrap.querySelector('[data-tw-exp]');
    const nav = wrap.querySelector('[data-tw-exp-nav]');
    if (!row || !nav) return;
    const room = row.scrollWidth - row.clientWidth;
    nav.hidden = room < SLACK;
    const [prev, next] = nav.querySelectorAll('.tw-exp-btn');
    const from = Math.abs(row.scrollLeft);
    if (prev) prev.disabled = from < SLACK;
    if (next) next.disabled = from > room - SLACK;
  };

  const syncAll = () => document.querySelectorAll('[data-tw-exp-wrap]').forEach(sync);

  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('.tw-exp-btn');
    if (!button) return;
    const wrap = button.closest('[data-tw-exp-wrap]');
    const row = wrap && wrap.querySelector('[data-tw-exp]');
    if (!row) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const direction = Number(button.dataset.dir) || 1;
    const rtl = getComputedStyle(row).direction === 'rtl' ? -1 : 1;
    // One step is the visible width, less a little so the card that was last stays in view as the first.
    row.scrollBy({ left: (row.clientWidth - 60) * direction * rtl, behavior: reduce ? 'auto' : 'smooth' });
  });

  document.addEventListener('scroll', (event) => {
    const row = event.target;
    if (row?.matches?.('[data-tw-exp]')) sync(row.closest('[data-tw-exp-wrap]'));
  }, { capture: true, passive: true });

  window.addEventListener('resize', syncAll);
  window.addEventListener('load', syncAll);

  // Cards that arrive later (Shopify's related products, the theme editor) are measured as they land.
  // Only changes inside a rail, or a rail being added, matter: the page has hundreds of unrelated DOM changes while it
  // loads (apps, tracking, gallery), and measuring the rows for each of them forced a layout every time.
  const WRAP = '[data-tw-exp-wrap]';
  const concerns = (mutation) => {
    const target = mutation.target;
    if (target.nodeType === 1 && (target.matches(WRAP) || target.closest(WRAP))) return true;
    for (const node of mutation.addedNodes) {
      if (node.nodeType === 1 && (node.matches(WRAP) || node.querySelector(WRAP))) return true;
    }
    return false;
  };
  let queued = false;
  new MutationObserver((mutations) => {
    if (queued || !mutations.some(concerns)) return;
    queued = true;
    window.requestAnimationFrame(() => {
      queued = false;
      syncAll();
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  syncAll();
})();
